import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrawingContext } from './DrawingContext';
import { useOpenLayersContext } from '../OpenLayersContext';
import { useLayers } from '../layerManagement/LayerContext';
import { shapeToFeature } from '../tranformations/shapeToFeature';
import { featureToShape } from '../featureToShape';
import { DrawingStyles } from '../styles/getDrawingStyle';
import {
  baseColours,
  strokeColours,
  getHslaColor,
} from '../styles/baseColours';
import { Fill, Style, Stroke } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import { Geometry, createGeoJsonFromGeometry } from '@ais-ui/utils';

// ---------- Types ----------

/** A single shape in a multi-shape drawing session */
export interface DrawingShape {
  /** Unique identifier for this shape */
  id: string;
  /** Human-readable label shown in selectors */
  label: string;
  /** Color name (from baseColours: 'blue', 'orange', 'red', etc.) used for reference layers */
  color: string;
  /** Transparency for reference layers (0 = opaque, 1 = fully transparent) */
  transparency?: number;
  /** Drawing style to use when this shape is active */
  drawingStyle: DrawingStyles;
  /** Current geometry (may be modified during the session) */
  geometry: Geometry | null;
}

interface MultiShapeDrawingContextValue {
  /** Whether a multi-shape drawing session is currently active */
  isActive: boolean;
  /** All shapes in the current session */
  shapes: DrawingShape[];
  /** ID of the shape currently being drawn/edited */
  activeShapeId: string | null;
  /** Start a multi-shape drawing session */
  activate: (shapes: DrawingShape[], activeId: string) => void;
  /** End the session and return all (possibly modified) shapes. Cleans up reference layers. */
  deactivate: () => DrawingShape[];
  /** Switch to editing a different shape. Saves current geometry, swaps reference layers. */
  switchShape: (id: string) => void;
  /** Add a new shape to the session and switch to it */
  addShape: (shape: DrawingShape) => void;
  /** Get all shapes with the current drawing baked into the active shape */
  getAllShapes: () => DrawingShape[];
}

const MultiShapeDrawingCtx =
  createContext<MultiShapeDrawingContextValue | null>(null);

// ---------- Reference layer helpers ----------

const REF_LAYER_PREFIX = 'multi-shape-ref-';

function refLayerId(shapeId: string) {
  return `${REF_LAYER_PREFIX}${shapeId}`;
}

// ---------- Provider ----------

interface MultiShapeDrawingProviderProps {
  children: ReactNode;
}

/**
 * Generic provider for multi-shape drawing sessions.
 *
 * Mount once inside DrawingProvider (e.g. in MapPanel). It stays dormant
 * until `activate()` is called. Manages reference layers for non-active
 * shapes and supports switching between shapes via `switchShape()`.
 *
 * Usage:
 * ```tsx
 * const ctx = useMultiShapeDrawing();
 * ctx.activate(shapes, 'shape-1');  // start session
 * ctx.switchShape('shape-2');        // switch active shape
 * const result = ctx.deactivate();   // end session, get all shapes back
 * ```
 */
export const MultiShapeDrawingProvider = ({
  children,
}: MultiShapeDrawingProviderProps) => {
  const [shapes, setShapes] = useState<DrawingShape[]>([]);
  const [activeShapeId, setActiveShapeId] = useState<string | null>(null);
  const isActive = shapes.length > 0 && activeShapeId !== null;

  const { drawnFeature, setDrawingStyle, replaceFeature, clearDrawing } =
    useDrawingContext();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { addLayer, removeLayerById } = useLayers();

  // Refs for values needed in callbacks to avoid stale closures
  const shapesRef = useRef(shapes);
  shapesRef.current = shapes;
  const activeShapeIdRef = useRef(activeShapeId);
  activeShapeIdRef.current = activeShapeId;

  // Track reference layer IDs for cleanup
  const refLayerIdsRef = useRef<Set<string>>(new Set());

  // ---- Reference layer management ----

  const addRefLayer = useCallback(
    (shape: DrawingShape) => {
      if (!shape.geometry) return;
      const layerId = refLayerId(shape.id);
      // Remove existing before re-adding (geometry may have changed)
      removeLayerById(layerId);
      refLayerIdsRef.current.delete(layerId);

      // Build feature from geometry
      const geoJson = createGeoJsonFromGeometry(shape.geometry);
      const feature = shapeToFeature(
        geoJson,
        displayProjection,
        dataProjection,
      );
      if (!feature?.getGeometry()) return;

      const vectorSource = new VectorSource<Feature<OlGeometry>>();
      vectorSource.addFeature(feature);

      // Subdued style: thin dashed stroke, low-opacity fill
      const colorKey = shape.color as keyof typeof baseColours;
      const fillColor = getHslaColor(
        baseColours[colorKey] || baseColours.blue,
        1 - (shape.transparency ?? 0.7),
      );
      const strokeColor = getHslaColor(
        strokeColours[colorKey] || strokeColours.blue,
        0.5,
      );

      const layer = new VectorLayer({
        source: vectorSource,
        style: new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({
            color: strokeColor,
            width: 2,
            lineDash: [6, 4],
          }),
        }),
      });

      addLayer(
        layer,
        shape.label,
        false,
        false,
        false,
        { id: layerId, type: 'multi-shape-reference' },
        layerId,
        false,
        false,
        false,
        'top',
      );
      refLayerIdsRef.current.add(layerId);
    },
    [addLayer, removeLayerById, displayProjection, dataProjection],
  );

  const removeRefLayer = useCallback(
    (shapeId: string) => {
      const layerId = refLayerId(shapeId);
      removeLayerById(layerId);
      refLayerIdsRef.current.delete(layerId);
    },
    [removeLayerById],
  );

  const removeAllRefLayers = useCallback(() => {
    refLayerIdsRef.current.forEach((id) => removeLayerById(id));
    refLayerIdsRef.current.clear();
  }, [removeLayerById]);

  // ---- Read current drawn feature as Geometry ----

  const readCurrentFeatureGeometry = useCallback((): Geometry | undefined => {
    if (!drawnFeature.current) return undefined;
    const geoJson = featureToShape(
      drawnFeature.current,
      displayProjection,
      dataProjection,
    );
    if (!geoJson?.geometry) return undefined;
    return geoJson.geometry as unknown as Geometry;
  }, [drawnFeature, displayProjection, dataProjection]);

  // ---- Activate ----

  const activate = useCallback(
    (initialShapes: DrawingShape[], initialActiveId: string) => {
      setShapes(initialShapes);
      setActiveShapeId(initialActiveId);

      // Add reference layers for non-active shapes
      initialShapes.forEach((s) => {
        if (s.id !== initialActiveId) addRefLayer(s);
      });
    },
    [addRefLayer],
  );

  // ---- Deactivate ----

  const deactivate = useCallback((): DrawingShape[] => {
    const currentGeom = readCurrentFeatureGeometry();
    const result = shapesRef.current.map((s) =>
      s.id === activeShapeIdRef.current && currentGeom
        ? { ...s, geometry: currentGeom }
        : s,
    );

    removeAllRefLayers();
    setShapes([]);
    setActiveShapeId(null);

    return result;
  }, [readCurrentFeatureGeometry, removeAllRefLayers]);

  // ---- Switch shape ----

  const switchShape = useCallback(
    (targetId: string) => {
      const currentActiveId = activeShapeIdRef.current;
      if (targetId === currentActiveId || !currentActiveId) return;

      // 1. Save current feature geometry
      const savedGeom = readCurrentFeatureGeometry();

      // 2. Update shapes state and add reference layer for the shape we're leaving
      setShapes((prev) => {
        const updated = prev.map((s) =>
          s.id === currentActiveId && savedGeom
            ? { ...s, geometry: savedGeom }
            : s,
        );

        const leavingShape = updated.find((s) => s.id === currentActiveId);
        if (leavingShape) addRefLayer(leavingShape);

        return updated;
      });

      // 3. Remove reference layer for target
      removeRefLayer(targetId);

      // 4. Load target shape into drawing context
      const targetShape = shapesRef.current.find((s) => s.id === targetId);
      if (targetShape?.geometry) {
        const geoJson = createGeoJsonFromGeometry(targetShape.geometry);
        const feature = shapeToFeature(
          geoJson,
          displayProjection,
          dataProjection,
        );
        if (feature && replaceFeature.current) {
          drawnFeature.current = feature;
          replaceFeature.current(feature);
        }
      } else if (clearDrawing.current) {
        drawnFeature.current = null;
        clearDrawing.current();
      }

      // 5. Switch drawing style
      if (targetShape) {
        setDrawingStyle(targetShape.drawingStyle);
      }

      setActiveShapeId(targetId);
    },
    [
      readCurrentFeatureGeometry,
      addRefLayer,
      removeRefLayer,
      setDrawingStyle,
      drawnFeature,
      replaceFeature,
      clearDrawing,
      displayProjection,
      dataProjection,
    ],
  );

  // ---- Add new shape and switch to it ----

  const addShape = useCallback(
    (shape: DrawingShape) => {
      setShapes((prev) => [...prev, shape]);
      // Defer switch so the new shape is in state
      setTimeout(() => switchShape(shape.id), 0);
    },
    [switchShape],
  );

  // ---- Get all shapes with current drawing baked in ----

  const getAllShapes = useCallback((): DrawingShape[] => {
    const currentGeom = readCurrentFeatureGeometry();
    return shapesRef.current.map((s) =>
      s.id === activeShapeIdRef.current && currentGeom
        ? { ...s, geometry: currentGeom }
        : s,
    );
  }, [readCurrentFeatureGeometry]);

  const value = useMemo<MultiShapeDrawingContextValue>(
    () => ({
      isActive,
      shapes,
      activeShapeId,
      activate,
      deactivate,
      switchShape,
      addShape,
      getAllShapes,
    }),
    [
      isActive,
      shapes,
      activeShapeId,
      activate,
      deactivate,
      switchShape,
      addShape,
      getAllShapes,
    ],
  );

  return (
    <MultiShapeDrawingCtx.Provider value={value}>
      {children}
    </MultiShapeDrawingCtx.Provider>
  );
};

// ---------- Hook ----------

/** Returns multi-shape drawing context, or null if no provider is mounted. */
export function useMultiShapeDrawing() {
  return useContext(MultiShapeDrawingCtx);
}
